import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { Modal } from '@/components/common/Modal/Modal';
import type { TitleType } from '@/components/menu/Tile/Tile';
import { Tile, TileKey } from '@/components/menu/Tile/Tile';
import ViewCartContainer from '@/components/menu/ViewCartContainer/ViewCartContainer';
import { CategoryLinks } from '@/components/menuV2/CategoryLinks/CategoryLinks';
import { FilterSortContainerDesktop } from '@/components/menuV2/FilterSortContainerDesktop/FilterSortContainerDesktop';
import { MenuBuilder } from '@/components/menuV2/MenuBuilder/MenuBuilder';
import PageLinksMobile from '@/components/menuV2/PageLinksMobile/PageLinksMobile';
import { useScrollDirection } from '@/hooks';
import type {
  FilterRecordModelType,
  MenuHierarchyRecordModelType,
  MenuSortOptionRecordModelType,
  MenuTileRecordModelType,
} from '@/models/api';
import type { Banner } from '@/models/cart/banner';
import { useStore } from '@/models/root-store';
import type { MenuPath } from '@/shared/types';
import { createFilterLookup } from '@/utils/api-helpers';
import { LoadingState } from '@/utils/constants';
import { getScrollStyles } from '@/utils/helpers';

import { FilterSortContainerMobile } from '../FilterSortContainerMobile/FilterSortContainerMobile';

interface MenuPageProps {
  menuHierarchyCms: MenuHierarchyRecordModelType;
  filterCmsData: FilterRecordModelType;
  sortCmsData: MenuSortOptionRecordModelType[];
  categoryLinks: MenuHierarchyRecordModelType[];
  categoryMenuHierarchy: MenuHierarchyRecordModelType;
  activeTiles?: MenuTileRecordModelType[];
  menuPath: MenuPath;
}

const MenuPage = observer(
  ({
    menuHierarchyCms,
    filterCmsData,
    sortCmsData,
    categoryLinks,
    categoryMenuHierarchy,
    menuPath,
  }: MenuPageProps) => {
    const showBulkDiscountProgressBar = Boolean(
      menuHierarchyCms?.showBulkDiscountBar,
    );
    const {
      productStore: {
        productState,
        filters,
        getNewFilteredProducts,
        setProductCollection,
        search: { resetSearch, isSearchPending },
      },
      customerStore: { isLoggedIn, getCustomerProfile },
      cartStore: { draftCart, getPromoBanner, banner },
      generalStore: { ready },
    } = useStore();

    const {
      setProductWhereInput,
      setShowOnlyLikedFilter,
      filterKeyLookupEmpty,
    } = filters;
    const router = useRouter();
    const { code } = router.query;
    const extractedPromoCode = _.isArray(code) ? _.first(code) : code || '';
    const filteredTile = (banner as Banner)?.getBannerByKey(TileKey.Menu);
    const isGuest = !isLoggedIn;
    useEffect(() => {
      if (draftCart?.action) {
        draftCart.setAction(undefined);
      }
      resetSearch();
    }, []);

    useEffect(() => {
      if (menuPath.category === 'liked-products') {
        setShowOnlyLikedFilter(true);
      } else {
        setShowOnlyLikedFilter(false);
      }

      if (productState === LoadingState.DONE) {
        setProductWhereInput(menuHierarchyCms?.productWhereInput ?? {});
        setProductCollection('default');
        getNewFilteredProducts();
      }
    }, [productState, router.asPath]);

    useEffect(() => {
      if (filterKeyLookupEmpty) {
        const filterKeyLookup =
          filterCmsData && createFilterLookup(filterCmsData.sections);
        filters.setFilterKeyLookup(filterKeyLookup);
        const sortKeyLookup = _.keyBy(sortCmsData, 'value');
        filters.setSortKeyLookup(sortKeyLookup);
      }
    }, [productState]);

    useEffect(() => {
      if (isLoggedIn) {
        getCustomerProfile().then(getNewFilteredProducts);
      }
    }, [isLoggedIn]);

    useEffect(() => {
      if (extractedPromoCode && ready) {
        getPromoBanner(extractedPromoCode);
      }
    }, [extractedPromoCode, ready]);

    const scrollDirection = useScrollDirection();
    // const hide = scrollDirection === 'down' ? 'top-[21px]' : 'top-[64px]';
    const hide = getScrollStyles(scrollDirection, draftCart.mode);
    const updatedCategoryLinks = categoryLinks.filter(
      (link) => !(link.slug === 'liked-products' && isGuest),
    );
    return (
      <>
        <div className="container flex h-full px-3 lg:px-2 lg:py-4 xl:p-8">
          <FilterSortContainerDesktop
            filterCmsData={filterCmsData}
            sortCmsData={sortCmsData}
            categoryMenuHierarchy={categoryMenuHierarchy}
            menuHierarchyCms={menuHierarchyCms}
            router={router}
            menuPath={menuPath}
            isSearchPending={isSearchPending}
          />

          <div className="w-full pb-8 md:px-8">
            <div
              className={`sticky z-30 bg-white pt-3 transition-all duration-500 ease-in-out lg:static lg:pt-0 ${hide}`}
            >
              <CategoryLinks
                links={updatedCategoryLinks}
                path={menuPath.category || ''}
                router={router}
              />
              <div className="lg:hidden">
                <PageLinksMobile
                  categoryMenuHierarchy={categoryMenuHierarchy}
                  currentPath="menu"
                  menuPath={menuPath}
                  router={router}
                />
              </div>
            </div>

            {filteredTile && (
              <div className="">
                <Tile
                  code={banner?.code || ''}
                  title={filteredTile?.title || ''}
                  description={filteredTile?.description || ''}
                  titleType={(filteredTile?.titleType || 'code') as TitleType}
                  borderColor={filteredTile?.borderColor}
                  backgroundColor={filteredTile?.backgroundColor}
                  textHighlightColor={filteredTile?.textHighlightColor}
                />
              </div>
            )}

            <MenuBuilder menuHierarchyCms={menuHierarchyCms} />
          </div>
        </div>
        <Modal
          isOpen={filters.isFilterOpen}
          onRequestClose={() => filters.closeFilter()}
          customContentStyles={{ maxWidth: 570 }}
          hideCloseIcon
        >
          <FilterSortContainerMobile
            filterCmsData={filterCmsData}
            sortCmsData={sortCmsData}
            primaryCategory={categoryMenuHierarchy.name || 'No Products Found'}
          />
        </Modal>
        <ViewCartContainer
          showBulkDiscountProgressBarOnMobile={showBulkDiscountProgressBar}
        />
      </>
    );
  },
);

export default MenuPage;
